import { DEPRECATED_PREFERENCE_FIELDS } from '../../types/reportContractsV2';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stripUnboundDeprecatedPreferenceFields(
  domain: string,
  boundValue: unknown,
  liveValue: unknown,
): unknown {
  if (!record(boundValue) || !record(liveValue)) return liveValue;
  const deprecatedFields =
    DEPRECATED_PREFERENCE_FIELDS[domain as keyof typeof DEPRECATED_PREFERENCE_FIELDS];
  if (!deprecatedFields || deprecatedFields.length === 0) return liveValue;
  const deprecatedSet = new Set<string>(deprecatedFields);
  return Object.fromEntries(
    Object.entries(liveValue).filter(
      ([field]) => !deprecatedSet.has(field) || field in boundValue,
    ),
  );
}

function effectiveDomainPreferences(domain: string, value: unknown): unknown {
  if (domain !== 'public-sentiment' || (value != null && !record(value))) return value;
  const preferences = (value ?? {}) as Record<string, unknown>;
  // The source collector already applies these defaults. Explicit defaults
  // and an omitted setting consume identical inputs; non-default values and
  // additional preferences remain part of the comparison.
  return { ...preferences, trendWindowDays: preferences.trendWindowDays ?? 365,
    includeTribalRecords: preferences.includeTribalRecords ?? false };
}

/** Compare only consumed domains/fields, identically at commit and read time. */
export function boundTypedPreferencesMatch(domain: string, bound: unknown, live: unknown): boolean {
  if (!record(bound)) return bound == null;
  const liveMap = record(live) ? live : {};
  const projected: Record<string, unknown> = {};
  const normalizedBound: Record<string, unknown> = {};
  for (const [key, boundValue] of Object.entries(bound)) {
    const rawLiveValue = stripUnboundDeprecatedPreferenceFields(key, boundValue, liveMap[key]);
    const projectable = key !== domain && record(boundValue) && record(rawLiveValue);
    projected[key] = effectiveDomainPreferences(key, projectable
      ? Object.fromEntries(Object.keys(boundValue).map(field => [field, rawLiveValue[field]])) : rawLiveValue);
    normalizedBound[key] = effectiveDomainPreferences(key, boundValue);
  }
  return hashCanonicalJson(projected) === hashCanonicalJson(normalizedBound);
}
